import type { FlexProps, SystemStyleObject } from "@chakra-ui/react";
import { Button, Flex } from "@chakra-ui/react";
import { debounce } from "lodash-es";
import type { FC, JSX } from "react";
import { useCallback, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { focusOutline } from "../../../css/02.focus/focus.styles";
import { Box } from "../../layout/box/Box";
import { Text } from "../text/Text";
import { styles } from "./ReadMore.styles";

export interface ReadMoreProps extends FlexProps {
    ariaMore?: string;
    ariaLess?: string;
    showMoreLabel?: string;
    showLessLabel?: string;
    collapsedHeight: string[];
    text?: string;
    textStyle?: object;
    noOfLines?: number;
    showButtonStyle?: SystemStyleObject;
    activateReadMore?: boolean;
}

export const DEFAULT_SHOW_MORE_LABEL = "_Read more";
export const DEFAULT_SHOW_LESS_LABEL = "_Read less";
const DEFAULT_COLLAPSED_HEIGHT = ["2rem"];
const DEFAULT_NUMBER_OF_LINES = 2;

const getCollapsedHeight = (el: HTMLElement) => {
    const { maxHeight } = getComputedStyle(el);
    const parsed = Number.parseFloat(maxHeight);

    return maxHeight.endsWith("px") && !Number.isNaN(parsed) ? parsed : el.clientHeight;
};

export const READ_MORE_TEST_ID = "readmore-content";

export const ReadMore: FC<ReadMoreProps> = ({
    children,
    showMoreLabel = DEFAULT_SHOW_MORE_LABEL,
    showLessLabel = DEFAULT_SHOW_LESS_LABEL,
    collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
    text,
    textStyle,
    noOfLines = DEFAULT_NUMBER_OF_LINES,
    showButtonStyle,
    activateReadMore = true,
    ...rest
}): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showButton, setShowButton] = useState<boolean>(false);
    const [openHeight, setOpenHeight] = useState<string | "auto">("auto");

    const ref = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLParagraphElement>(null);
    const isCollapsingRef = useRef<boolean>(false);

    const handleToggle = () => {
        setIsExpanded((prevState) => {
            const nextIsExpanded = !prevState;

            if (prevState && !nextIsExpanded) {
                isCollapsingRef.current = true;
            }

            return nextIsExpanded;
        });
    };

    const checkButtonAvailability = useCallback(() => {
        const box = ref.current;
        const content = contentRef.current;

        if (!box || !content) {
            return;
        }

        const hasOverflow = content.scrollHeight > getCollapsedHeight(box);

        if (isCollapsingRef.current) {
            return;
        }

        setShowButton(hasOverflow);
    }, []);

    const checkOpenHeight = useCallback(() => {
        const box = ref.current;

        if (!box) {
            return;
        }

        setOpenHeight(box.scrollHeight + "px");
    }, []);

    useIsomorphicLayoutEffect(() => {
        if (isExpanded) {
            checkOpenHeight();
            return;
        }

        if (isCollapsingRef.current) {
            return;
        }

        checkButtonAvailability();
    }, [checkButtonAvailability, checkOpenHeight, isExpanded, text]);

    useIsomorphicLayoutEffect(() => {
        const box = ref.current;

        if (!box) {
            return;
        }

        const handleTransitionEnd = (event: TransitionEvent) => {
            if (event.target !== box || event.propertyName !== "max-height" || isExpanded) {
                return;
            }

            isCollapsingRef.current = false;
            checkButtonAvailability();
        };

        box.addEventListener("transitionend", handleTransitionEnd);

        return () => {
            box.removeEventListener("transitionend", handleTransitionEnd);
        };
    }, [checkButtonAvailability, isExpanded]);

    useIsomorphicLayoutEffect(() => {
        const debouncedCheck = debounce(() => {
            if (isExpanded) {
                checkOpenHeight();
                return;
            }

            if (isCollapsingRef.current) {
                return;
            }

            checkButtonAvailability();
        }, 300);

        window.addEventListener("resize", debouncedCheck);

        return () => {
            debouncedCheck.cancel();
            window.removeEventListener("resize", debouncedCheck);
        };
    }, [checkButtonAvailability, checkOpenHeight, isExpanded]);

    useIsomorphicLayoutEffect(() => {
        const content = contentRef.current;

        if (!content) {
            return;
        }

        const runCheck = () => {
            if (isExpanded) {
                checkOpenHeight();
                return;
            }

            if (isCollapsingRef.current) {
                return;
            }

            checkButtonAvailability();
        };

        // Some pages settle text layout shortly after mount (fonts/content hydration).
        // Defer one measurement to avoid requiring a scroll/resize interaction.
        const timeoutId = window.setTimeout(runCheck, 50);
        const rafId = window.requestAnimationFrame(runCheck);

        return () => {
            window.clearTimeout(timeoutId);
            window.cancelAnimationFrame(rafId);
        };
    }, [checkButtonAvailability, checkOpenHeight, isExpanded, text]);

    return (
        <Flex flexDirection="column" alignItems="flex-start" {...rest}>
            <Box
                data-testid={READ_MORE_TEST_ID}
                ref={ref}
                overflow="hidden"
                maxHeight={
                    !activateReadMore ? "none" : isExpanded ? `${openHeight}` : collapsedHeight
                }
                transitionDuration={"0.3s"}
                transitionProperty={"max-height, height"}
            >
                <Text
                    ref={contentRef}
                    {...textStyle}
                    noOfLines={
                        showButton && !isExpanded && activateReadMore ? noOfLines : undefined
                    }
                >
                    {text}
                </Text>
            </Box>

            {showButton && activateReadMore && (
                <Button
                    aria-label={isExpanded ? showLessLabel : showMoreLabel}
                    background="none"
                    _hover={{ background: "none" }}
                    _focusVisible={{ ...focusOutline, background: "none" }}
                    display="flex"
                    onClick={handleToggle}
                    h="100%"
                    variant="link"
                    textDecoration="underline"
                    sx={styles.showButton}
                >
                    <Text color="black">{isExpanded ? showLessLabel : showMoreLabel}</Text>
                </Button>
            )}
        </Flex>
    );
};
